import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Newspaper } from "lucide-react";
import SEO from "@/components/common/SEO";
import Breadcrumbs from "@/components/common/Breadcrumbs";
import { Skeleton } from "@/components/ui/skeleton";
import FinalCTA from "@/components/sections/FinalCTA";
import api from "@/lib/api";

function formatDate(iso) {
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }); }
  catch { return ""; }
}

export default function InsightPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(undefined); // undefined = loading, null = not found

  useEffect(() => {
    api.get(`/posts/${slug}`).then((r) => setPost(r.data)).catch(() => setPost(null));
  }, [slug]);

  if (post === undefined) {
    return (
      <div className="container-page py-24 max-w-3xl">
        <Skeleton className="h-8 w-2/3" />
        <Skeleton className="h-4 w-1/3 mt-4" />
        <Skeleton className="w-full aspect-video mt-8 rounded-lg" />
      </div>
    );
  }

  if (post === null) {
    return (
      <div className="container-page py-32 text-center min-h-[60vh] flex flex-col items-center justify-center" data-testid="insight-notfound">
        <Newspaper className="h-10 w-10 text-teal/50" />
        <h1 className="mt-6 font-heading font-bold text-[28px] text-navy">Article not found</h1>
        <Link to="/insights" className="mt-4 text-teal font-semibold inline-flex items-center gap-1">
          <ArrowLeft className="h-4 w-4" /> Back to insights
        </Link>
      </div>
    );
  }

  return (
    <>
      <SEO
        title={`${post.title} | Premier Global Sourcing`}
        description={post.excerpt || post.title}
        path={`/insights/${post.slug}`}
        image={post.cover_image}
        type="article"
      />
      <div className="bg-navy text-white [&_a]:text-white/60 [&_span]:text-white/90 [&_.text-muted-foreground]:text-white/50">
        <Breadcrumbs items={[{ label: "Insights", path: "/insights" }, { label: post.title }]} />
      </div>
      <article className="container-page py-14 lg:py-20 max-w-3xl">
        <div className="flex items-center gap-3 text-[13px] text-muted-foreground">
          <span className="font-semibold uppercase tracking-wide text-teal">{post.category}</span>
          <span>·</span>
          <span>{formatDate(post.created_at)}</span>
          {post.author && <><span>·</span><span>{post.author}</span></>}
        </div>
        <h1 className="mt-4 font-heading font-bold tracking-tight text-navy text-[34px] leading-[40px] lg:text-[46px] lg:leading-[54px]">
          {post.title}
        </h1>
        {post.excerpt && <p className="mt-5 text-[19px] leading-[30px] text-muted-foreground">{post.excerpt}</p>}

        {post.cover_image && (
          <div className="mt-8 overflow-hidden rounded-lg border border-[#D9E0E4]">
            <img src={post.cover_image} alt={post.title} className="w-full aspect-video object-cover" />
          </div>
        )}

        <div className="mt-10 space-y-5" data-testid="insight-body">
          {post.body.split("\n").filter((p) => p.trim()).map((para, i) => (
            <p key={i} className="text-[16.5px] leading-[28px] text-[#2A3742]">{para}</p>
          ))}
        </div>

        <div className="mt-12 pt-8 border-t border-[#D9E0E4]">
          <Link to="/insights" className="text-teal font-semibold inline-flex items-center gap-1">
            <ArrowLeft className="h-4 w-4" /> Back to all insights
          </Link>
        </div>
      </article>
      <FinalCTA />
    </>
  );
}
